/**
 * History breadcrumb trail.
 *
 * Drill-down navigations (list → idea → brief → ...) stamp the chain of
 * visited pages into router ``location.state.trail``. Detail pages publish
 * the chain to the header (see TrailDisplayContext), which renders it as a
 * clickable breadcrumb row under the tab group.
 *
 * Because the trail rides in per-history-entry router state, tab clicks,
 * client switches, and any other unstamped navigation reset it for free,
 * and browser back/forward rewind it correctly.
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export interface TrailCrumb {
  /** Absolute path (incl. search) to return to. */
  to: string;
  /** Human label for the crumb. */
  label: string;
}

function crumbPathname(to: string): string {
  return to.split("?")[0];
}

/**
 * Extract the incoming trail from router state. Malformed state yields [].
 * Revisit rule: navigating to a page already in the trail acts like
 * clicking its crumb — everything from that entry on is dropped (the page
 * itself becomes the current crumb again).
 */
export function readTrail(state: unknown, currentPathname: string): TrailCrumb[] {
  const raw = (state as { trail?: unknown } | null | undefined)?.trail;
  if (!Array.isArray(raw)) return [];
  const trail = raw.filter(
    (c): c is TrailCrumb =>
      typeof c === "object" &&
      c !== null &&
      typeof (c as TrailCrumb).to === "string" &&
      typeof (c as TrailCrumb).label === "string"
  );
  const revisit = trail.findIndex((c) => crumbPathname(c.to) === currentPathname);
  return revisit === -1 ? trail : trail.slice(0, revisit);
}

/** Display cap: show the last ``max`` crumbs, ellipsing the head. */
export function visibleTrail<T>(
  crumbs: T[],
  max = 3
): { ellipsed: boolean; visible: T[] } {
  if (crumbs.length <= max) return { ellipsed: false, visible: crumbs };
  return { ellipsed: true, visible: crumbs.slice(-max) };
}

/** The normalized trail the current location arrived with. */
export function useIncomingTrail(): TrailCrumb[] {
  const location = useLocation();
  return useMemo(
    () => readTrail(location.state, location.pathname),
    [location.state, location.pathname]
  );
}

/**
 * Entry-point side. Options for ``navigate``/``Link`` that append the
 * current page to the trail. ``label`` is the CURRENT page's display name
 * (e.g. ``ENTITY_CONFIG.idea.plural`` on the ideas list).
 */
export function useTrailNavOptions(label: string): { state: { trail: TrailCrumb[] } } {
  const location = useLocation();
  const incoming = useIncomingTrail();
  return useMemo(
    () => ({
      state: {
        trail: [...incoming, { to: location.pathname + location.search, label }],
      },
    }),
    [incoming, location.pathname, location.search, label]
  );
}
